/* eslint-disable react/prop-types */
import type { FileCell } from '../types'
import clsx from 'clsx'
import { useCallback, useState, type FC, type HTMLProps } from 'react'
import { AppWindow, AudioLines, CaseSensitive, File, FileText, Image, Video } from 'lucide-react'
import { Copy, Check, Upload } from 'lucide-react'
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../shadcn'
import { cn, hashIdToColor } from '../../../../lib/utils'
import { createRenderer } from './renderer'
import { formatDate } from '@bpinternal/shared'
import { Dropzone, DropzoneContent, DropzoneEmptyState } from '../../dropzone'
import { NOL } from './null'
import { showErrorToast } from '../../Toaster'
import { Spinner } from '../../spinner'

export const Files = createRenderer<FileCell>({
  View: ({ className, cell }) => {
    const { url, name, value, contentType } = cell

    if (!value) {
      return <NOL />
    }

    return (
      <div className="flex justify-start items-center w-full">
        <FileIcon contentType={contentType} />
        <p className={clsx(className)}>{name ?? url}</p>
      </div>
    )
  },
  Editor: ({ cell, onCellChange, uploadFile, ...props }) => {
    const { value } = cell
    const [isUploading, setIsUploading] = useState<boolean>(false)
    const handleDrop = useCallback(
      async (acceptedFiles: File[]) => {
        setIsUploading(true)
        const acceptedFile = acceptedFiles[0]
        if (!acceptedFile) return
        const uploadedFile = await uploadFile?.(acceptedFile)
        if (!uploadedFile) {
          showErrorToast('Unable to upload the file, please try again')
          setIsUploading(false)
          return
        }
        onCellChange?.(
          props.row,
          {
            type: 'file',
            value: uploadedFile.id,
            url: uploadedFile.url,
            name: uploadedFile.key,
            contentType: uploadedFile.contentType,
          },
          true
        )
        setIsUploading(false)
        props.onClose()
      },
      [uploadFile]
    )
    return (
      <Popover open>
        <PopoverTrigger asChild>
          <div className="flex justify-center items-center font-semibold w-full h-full cursor-pointer select-none">
            <Upload className="h-3" />
            <span>Upload</span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-80 text-xs" align="center" alignOffset={50}>
          {value ? (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="upload">Replace</TabsTrigger>
              </TabsList>
              <TabsContent value="info">
                <div className="space-y-2 pt-4">
                  <h4 className="font-medium leading-none">File Information</h4>
                  <p className="text-muted-foreground">Details about the stored file.</p>
                </div>
                <InfoView file={cell} />
              </TabsContent>
              <TabsContent value="upload">
                <div className="space-y-2 py-4">
                  <h4 className="font-medium leading-none">Replace File</h4>
                  <p className="text-muted-foreground">Upload a new file to replace the existing one.</p>
                </div>
                <Dropzone maxSize={100 * 1024 * 1024} maxFiles={1} minSize={1} onDrop={handleDrop}>
                  <DropzoneContent />
                  {isUploading ? (
                    <div className="text-muted-foreground flex flex-col items-center justify-center gap-4">
                      <p>Uploading File...</p>
                      <Spinner size="medium" />
                    </div>
                  ) : (
                    <DropzoneEmptyState />
                  )}
                </Dropzone>
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <div className="space-y-2 pb-4">
                <h4 className="font-medium leading-none">Upload File</h4>
                <p className="text-muted-foreground">Drag and drop a file or click to select.</p>
              </div>
              <Dropzone maxSize={100 * 1024 * 1024} maxFiles={1} onDrop={handleDrop}>
                {isUploading ? (
                  <div className="text-muted-foreground flex flex-col items-center justify-center gap-4">
                    <p>Uploading File...</p>
                    <Spinner size="medium" />
                  </div>
                ) : (
                  <DropzoneEmptyState />
                )}
                <DropzoneContent />
              </Dropzone>
            </>
          )}
        </PopoverContent>
      </Popover>
    )
  },
})

const InfoView: FC<{ file: FileCell }> = ({ file }) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const handleCopyUrl = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url ?? '').then(() => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
      })
    },
    [file.url, setIsCopied]
  )
  return (
    <div className="space-y-2 pt-4 text-xs">
      <InfoRow label="Name" value={file.name} />
      <InfoRow label="Size" value={formatFileSize(file.size)} />
      <div className="flex justify-between items-center">
        <span className="text-muted-foreground">URL</span>
        <div className="flex items-center gap-1">
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary hover:underline max-w-[180px] truncate"
          >
            {file.url}
          </a>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleCopyUrl(file.url ?? '')}>
            {isCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            <span className="sr-only">Copy URL</span>
          </Button>
        </div>
      </div>
      {file.tags && file.tags.length > 0 && (
        <div className="space-y-2">
          <span className="text-muted-foreground">Tags</span>
          <div className="flex flex-wrap gap-1">
            {file.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
      <InfoRow
        label="Created"
        value={file.createdAt ? formatDate(file.createdAt, { format: 'long', includeTime: true }) : null}
      />
      <InfoRow
        label="Modified"
        value={file.updatedAt ? formatDate(file.updatedAt, { format: 'long', includeTime: true }) : null}
      />
    </div>
  )
}
type FileType = 'image' | 'video' | 'audio' | 'text' | 'application' | 'font'

const contentTypeToFileType = (contentType: string): FileType =>
  contentType.startsWith('image/')
    ? 'image'
    : contentType.startsWith('video/')
      ? 'video'
      : contentType.startsWith('audio/')
        ? 'audio'
        : contentType.startsWith('text/')
          ? 'text'
          : contentType.startsWith('font/')
            ? 'font'
            : 'application'

type FileIconProps = { contentType?: string }

export const FileIcon: FC<FileIconProps & HTMLProps<HTMLDivElement>> = ({ contentType, ...props }) => {
  const fileType = contentTypeToFileType(contentType ?? '')

  const color = hashIdToColor(fileType)
  const { icon: Icon } = fileIconProps[fileType] ?? { icon: File }
  return (
    <div
      className={cn(
        `flex rounded-sm p-[1px] mr-2 text-${color}-500 bg-${color}-500 bg-opacity-15 dark:bg-opacity-30`,
        props.className
      )}
      {...props}
    >
      <Icon className={cn('size-4 rounded-md')} />
    </div>
  )
}

const fileIconProps: Partial<Record<FileType, { icon: FC<{ className?: string }> }>> = {
  image: { icon: Image },
  video: { icon: Video },
  audio: { icon: AudioLines },
  text: { icon: FileText },
  application: { icon: AppWindow },
  font: { icon: CaseSensitive },
}

// --- Helper Functions ---
function formatFileSize(bytes?: number, decimals = 2) {
  if (!bytes) return null
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) =>
  value && (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium truncate max-w-[220px]">{value}</span>
    </div>
  )
